/**
 * @module Hooks.UseSavedCard
 */
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "typedefs";
import { useActions } from "app/utils/hooks";
import { fetchPaymentInfo } from "app/actions/payment-info";
import { PaymentInfoModel } from "app/models";

interface UseSavedCardResult {
  paymentInfo: PaymentInfoModel | null;
  hasSavedCard: boolean;
  useNewCard: boolean;
  setUseNewCard: (value: boolean) => void;
  paymentInfoLoaded: boolean;
}

/**
 * Looks up the logged-in user's saved card (once, on mount) so a checkout form
 * can offer it as a payment option instead of the billing form. Shared across
 * the checkout forms so the state + lookup isn't reimplemented per screen.
 *
 * `paymentInfoLoaded` gates the render until the lookup resolves (so the full
 * billing form never flashes for someone who has a card on file).
 */
export function useSavedCard(): UseSavedCardResult {
  const paymentInfo = useSelector((state: RootState) => state.paymentInfo.data);
  const [useNewCard, setUseNewCard] = useState(false);
  const [paymentInfoLoaded, setPaymentInfoLoaded] = useState(false);
  const actions = useActions({ fetchPaymentInfo });

  useEffect(() => {
    let active = true;
    actions.fetchPaymentInfo().finally(() => {
      if (active) {
        setPaymentInfoLoaded(true);
      }
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    paymentInfo,
    // cardLast4 is the right signal (not cardId): the saved-card checkout submits
    // an empty token, and the backend subscribes on the Stripe customer's DEFAULT
    // payment method — cardId is never used to select a card. cardLast4 is read
    // from that same default PM, so its presence guarantees a submittable card.
    hasSavedCard: Boolean(paymentInfo?.cardLast4),
    useNewCard,
    setUseNewCard,
    paymentInfoLoaded,
  };
}
